import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Alert, Avatar, Badge, Button, Input } from '@dreamtree-org/twreact-ui';
import { Package, ChevronRight, LogOut } from 'lucide-react';
import { PageHeader, QueryState, SectionCard, AssistantConnections } from '@/components';
import { useMutation, useQuery } from '@/lib/useQuery';
import { useStorefront } from '../StorefrontContext';
import AddressForm from '../components/AddressForm';
import { servicePages } from '../lib/servicePages';

/** WEB-06 account: profile, saved addresses, shortcuts to orders and services. */
export default function Account() {
  const sf = useStorefront();
  const navigate = useNavigate();
  const q = useQuery('sfAccount', {}, { select: (r) => r.data });

  const signOut = async () => {
    await sf.logout();
    navigate('/', { replace: true });
  };

  return (
    <div className="space-y-4">
      <PageHeader title="My account" actions={<Button variant="outline" leftIcon={<LogOut className="h-4 w-4" />} onClick={signOut}>Sign out</Button>} />
      <QueryState query={q}>
        {(d) => (
          <div className="grid gap-5 lg:grid-cols-3">
            <div className="space-y-5 lg:col-span-2">
              <Profile customer={d.customer} onSaved={(s) => { sf.setSession(s); q.reload(); }} />
              <Addresses list={d.addresses} reload={q.reload} />
              <Assistants />
            </div>
            <div className="space-y-5">
              <SectionCard title="Shortcuts">
                <nav className="divide-y divide-[var(--app-border)]">
                  {[
                    [Package, 'My orders', 'Track, invoices, returns', '/account/orders'],
                    ...servicePages('account', sf.canVisit).map((p) => [p.Icon, p.title, p.sub, p.href]),
                  ].map(([Icon, title, sub, to]) => (
                    <Link key={to} to={to} className="flex min-h-14 items-center gap-3 py-2 hover:opacity-80">
                      <Icon className="h-5 w-5 text-primary-600" aria-hidden="true" />
                      <span className="flex-1"><span className="block text-sm font-semibold">{title}</span><span className="block text-xs opacity-70">{sub}</span></span>
                      <ChevronRight className="h-4 w-4 opacity-50" aria-hidden="true" />
                    </Link>
                  ))}
                </nav>
              </SectionCard>
            </div>
          </div>
        )}
      </QueryState>
    </div>
  );
}

function Profile({ customer, onSaved }) {
  const save = useMutation('sfProfileSave');
  const [name, setName] = useState(customer.name || '');
  const [email, setEmail] = useState(customer.email || '');
  const [done, setDone] = useState(false);
  useEffect(() => {
    setName(customer.name || '');
    setEmail(customer.email || '');
  }, [customer.name, customer.email]);

  const submit = async (e) => {
    e.preventDefault();
    setDone(false);
    try {
      const r = await save.run({ name, email });
      setDone(true);
      onSaved(r.data);
    } catch {
      /* shown below */
    }
  };

  return (
    <SectionCard title="Profile">
      <div className="flex items-center gap-3">
        <Avatar name={customer.name || 'Customer'} size="lg" />
        <div>
          <div className="font-semibold">{customer.name || 'Add your name'}</div>
          <div className="text-sm opacity-70">{customer.phone_masked} · verified by OTP</div>
        </div>
      </div>
      {!customer.name ? <Alert variant="info">Add your name so the courier and seller know who to deliver to.</Alert> : null}
      <form onSubmit={submit} className="grid gap-3 sm:grid-cols-2">
        <Input label="Full name" value={name} onChange={(e) => setName(e.target.value)} required autoComplete="name" />
        <Input label="Email (for invoices)" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
        <div className="flex flex-wrap items-center gap-3 sm:col-span-2">
          <Button type="submit" loading={save.loading}>Save profile</Button>
          {done ? <span className="text-sm text-success-700" role="status">Saved</span> : null}
        </div>
      </form>
      {save.error ? <Alert variant="error">{save.error}</Alert> : null}
    </SectionCard>
  );
}

function Addresses({ list, reload }) {
  const remove = useMutation('sfAddressRemove');
  const [editing, setEditing] = useState(null); // null | 'new' | address
  return (
    <SectionCard title="Saved addresses" actions={editing ? null : <Button size="sm" variant="outline" onClick={() => setEditing('new')}>Add address</Button>}>
      {list.length === 0 && !editing ? <p className="text-sm opacity-70">No saved addresses yet.</p> : null}
      <div className="grid gap-3 md:grid-cols-2">
        {list.map((a) => (
          <div key={a.id} className="space-y-2 rounded-lg border border-[var(--app-border)] p-3 text-sm">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-semibold">{a.name}</span>
              <Badge size="xs" variant="outline">{a.label}</Badge>
              {a.is_default ? <Badge size="xs" variant="primary">Default</Badge> : null}
            </div>
            <div className="opacity-80">{[a.line1, a.line2, a.city, a.state].filter(Boolean).join(', ')} {a.pin}</div>
            {a.phone ? <div className="text-xs opacity-60">+91 {a.phone}</div> : null}
            <div className="flex gap-2">
              <Button size="xs" variant="ghost" onClick={() => setEditing(a)}>Edit</Button>
              <Button size="xs" variant="ghost" loading={remove.loading} onClick={async () => { await remove.run({ id: a.id }); reload(); }}>Delete</Button>
            </div>
          </div>
        ))}
      </div>
      {editing ? (
        <div className="rounded-lg border border-dashed border-[var(--app-border)] p-3">
          <AddressForm initial={editing === 'new' ? null : editing} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} />
        </div>
      ) : null}
    </SectionCard>
  );
}

/** The shopper's connected AI assistants (D48): shop from Claude or ChatGPT; ordering stays on the store. */
function Assistants() {
  const q = useQuery('sfAssistants', {}, { select: (r) => r.data });
  const end = useMutation('sfAssistantDisconnect');
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  return (
    <QueryState query={q}>
      {(rows) => (
        <AssistantConnections
          title="AI assistants"
          connectUrls={[{ label: 'Shopping', url: `${origin}/api/mcp/shop` }]}
          rows={rows}
          onDisconnect={async (row) => { await end.run({ id: row.id }); q.reload(); }}
          emptyText="Connect an assistant to search, compare and fill your cart. You always check out and pay here yourself."
        />
      )}
    </QueryState>
  );
}
